import { Injectable } from '@angular/core';
import { AsyncSubject, BehaviorSubject, Observable, Subject } from 'rxjs';
import { MEDoNothingBasicResponseHandler, ResponseCode, BasicAsyncResponseSuccessHandler, MECustomTitleErrorResponseHandler } from 'src/app/common/services/api/me-api-response';
import { MEAPIService } from 'src/app/common/services/api/me-api.service';
import { MobileCheckinBean, MobileQueueListRequestBean, MobileQueueBean, QueueStatus, MobileUpdateQueueBean } from './mobile-queue-bean';
import { DateTimeService } from 'src/app/system/utility/datetime.service';
import { MEErrorService } from 'src/app/common/error/me-error.service';
import { TranslateUtilityService } from 'src/app/system/utility/translation-utility.service';
import { logger } from 'src/app/common/services/logging/logging.service';

@Injectable({
  providedIn: 'root'
})
export class MobileQueueService {

  readonly API_MOBILE_CHECKIN_CREATE: string = MEAPIService.BASE_URL + '/customer/mbQueue/create';
  readonly API_MOBILE_CHECKIN_LIST: string = MEAPIService.BASE_URL + '/customer/mbQueue/list';
  readonly API_MOBILE_CHECKIN_GET_BY_ID: string = MEAPIService.BASE_URL + '/customer/mbQueue/getById';
  readonly API_MOBILE_CHECKIN_REMOVE: string = MEAPIService.BASE_URL + '/customer/mbQueue/remove';
  readonly API_MOBILE_CHECKIN_UPDATE: string = MEAPIService.BASE_URL + '/customer/mbQueue/updateMessage';

  public pendingQueueSubject: BehaviorSubject<MobileQueueBean | null> = new BehaviorSubject<MobileQueueBean|null>(null);

  constructor(private srvApi: MEAPIService, private srvError: MEErrorService, private translate: TranslateUtilityService) { }

  public submitCreateQueue(bean: MobileCheckinBean): AsyncSubject<boolean> {
    return this.srvApi.Post(this.API_MOBILE_CHECKIN_CREATE, bean, [new MEDoNothingBasicResponseHandler(ResponseCode.SUCCESS)]);
  }

  public submitRemoveQueue(queueId: number): AsyncSubject<boolean> {
    let bean = { id: queueId };
    let ret = this.srvApi.Post(this.API_MOBILE_CHECKIN_REMOVE, bean, [new MEDoNothingBasicResponseHandler(ResponseCode.SUCCESS), new MEDoNothingBasicResponseHandler(ResponseCode.QUEUE_REQUEST_ALREADY_CLOSED)]);
    ret.subscribe((isOkay) => {
      if (isOkay) {
        this.invalidPendingQueue();
      }
    });
    return ret;
  }

  public getPendingQueue(isForceReload: boolean = false): AsyncSubject<MobileQueueBean | null> {
    logger.debug("GetPendingQueue : isForceReload", isForceReload);
    if (!isForceReload) {
      if (this.pendingQueueSubject.value) {
        let ret = new AsyncSubject<MobileQueueBean | null>();
        ret.next(this.pendingQueueSubject.value);
        ret.complete();
        return ret;
      }
    }

    let bean: MobileQueueListRequestBean = {
      "ascending": true,
      "locationId": null,
      "purposes": null,
      "queuedDateFrom": DateTimeService.getStartOfCurrentDay(),
      "queuedDateTo": DateTimeService.getEndOfCurrentDay(),
      "queuedUser": null,
      "queuedUserId": null,
      "routedTo": null,
      "statuses": [
        0,
        1,
        2
      ]
    }

    let ret = new AsyncSubject<MobileQueueBean|null>();
    let listRet = new AsyncSubject<MobileQueueBean[]>();
    listRet.subscribe((list) => {
      let queue = (list.length > 0) ? list[0] : null;
      this.pendingQueueSubject.next(queue);
      ret.next( queue);
      ret.complete();
    });

    // Trigger the Refresh API Call
    this.srvApi.Post<MobileQueueBean[]>(this.API_MOBILE_CHECKIN_LIST, bean, [new BasicAsyncResponseSuccessHandler<MobileQueueBean[]>(listRet)]);
    return ret;
  }

  public invalidPendingQueue() {
    this.pendingQueueSubject.next(null)
  }

  public updateQueuePosition(queuePosition: number) {
    if (!this.pendingQueueSubject.value) return;
    this.pendingQueueSubject.value.queuePosition = queuePosition;
  }

  public getQueueById(queueId: number): AsyncSubject<MobileQueueBean> {
    let bean = { id: queueId };
    let ret = new AsyncSubject<MobileQueueBean>();
    this.srvApi.Post<MobileQueueBean>(this.API_MOBILE_CHECKIN_GET_BY_ID, bean, [new BasicAsyncResponseSuccessHandler<MobileQueueBean>(ret)]);
    return ret;
  }

  public markHere(branch: MobileQueueBean): AsyncSubject<boolean> {
    let bean: MobileUpdateQueueBean = {
      creatorOnline: true,
      id: branch.id,
      locationId: branch.locationId,
      queuePosition: branch.queuePosition,
      status: QueueStatus.CUSTOMER_HERE
    }

    return this.srvApi.Post(this.API_MOBILE_CHECKIN_UPDATE, bean, [
      new MEDoNothingBasicResponseHandler(ResponseCode.SUCCESS),
      new MECustomTitleErrorResponseHandler(ResponseCode.QUEUE_REQUEST_ALREADY_CLOSED, this.srvError, this.translate.instant("MOBILE_QUEUE.ERROR_ALREADY_CLOSED"), this.translate.instant("MOBILE_QUEUE.ERROR_ALREADY_CLOSED_MESSAGE"))
    ]);
  }
}

// QUEUE_REQUEST_ALREADY_ROUTED					    = 4500, // Queue request already routed
// QUEUE_REQUEST_NOT_FOUND							    = 4501, // Queue request not found
// QUEUE_REQUEST_ALREADY_CLOSED					    = 4502, // Queue request already closed
// QUEUE_REQUEST_ALREADY_OPEN						    = 4503, // Queue request already open
// QUEUE_OUTSIDE_BUSINESS_HOURS					    = 4504, // Outside of business hours
// QUEUE_REQUEST_INVALID_LOCATION				    = 4505, // Invalid queue location
// QUEUE_REQUEST_NOT_ROUTED						      = 4506, // Queue request not routed
// QUEUE_REQUEST_MULTIPLE_ROUTING_FORBIDDEN	= 4507, // Multiple Routing Forbidden